import type {
  FieldEvent,
  FieldEventsResponse,
  FieldEventType,
  FieldRecordType,
  NewFieldEventInput,
} from '../types/fieldEvent';
import { FieldRecordTypes } from '../types/fieldEvent';
import type { FieldState } from '../types/fieldState';
import { apiClient } from './api/apiClient';

/**
 * 日付文字列（YYYY-MM-DD）をローカル日付としてパースする。
 * new Date("YYYY-MM-DD") は UTC midnight として解釈されるため、
 * タイムゾーンによっては前日にずれる問題を回避する。
 */
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Date を YYYY-MM-DD 形式の文字列に変換する。
 */
function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * API レスポンス型（日付は文字列として返る）
 */
interface FieldEventsApiResponse {
  fieldId: number;
  state: FieldState;
  events: Array<{
    id: number;
    fieldId: number;
    eventType: string;
    eventDate: string;
    recordType?: string;
    sequenceNumber: number;
    note: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
}

/**
 * 単一の FieldEvent API レスポンス（refreshEmergence 用）
 */
interface FieldEventApiResponse {
  id: number;
  fieldId: number;
  eventType: string;
  eventDate: string;
  recordType?: string;
  sequenceNumber: number;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * API レスポンスを FieldEvent ドメインモデルへ変換する
 */
function toFieldEvent(e: FieldEventApiResponse): FieldEvent {
  return {
    id: String(e.id),
    fieldId: String(e.fieldId),
    eventType: e.eventType as FieldEventType,
    eventDate: parseLocalDate(e.eventDate),
    recordType: (e.recordType as FieldRecordType) ?? FieldRecordTypes.ACTUAL,
    sequenceNumber: e.sequenceNumber,
    note: e.note,
    createdAt: new Date(e.createdAt),
    updatedAt: new Date(e.updatedAt),
  };
}

/**
 * 圃場イベントサービス
 * バックエンドAPIからFieldEventとステートを取得する
 *
 * ステート計算はバックエンド側で行う（asOf パラメータで基準日を指定）
 */
class FieldEventService {
  private cache: Map<string, FieldEventsResponse> = new Map();

  /**
   * キャッシュをクリアする
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * 圃場のイベント一覧とステートを取得する
   * @param fieldId - 圃場 ID
   * @param asOf - ステート計算の基準日（省略時はバックエンドがサーバーの現在日付で計算）
   * @returns イベント一覧とステート
   */
  async getFieldEvents(fieldId: string, asOf?: Date): Promise<FieldEventsResponse> {
    // asOf 指定時はキャッシュを使わない（日付が変わるたびに再取得が必要）
    if (!asOf) {
      const cached = this.cache.get(fieldId);
      if (cached) {
        return cached;
      }
    }

    const response = await this.getFieldEventsFromApi(fieldId, asOf);
    // asOf 指定時はキャッシュに保存しない（通常取得で過去日のレスポンスを返さないため）
    if (!asOf) {
      this.cache.set(fieldId, response);
    }
    return response;
  }

  /**
   * 圃場イベントを作成する
   * @param fieldId - 圃場 ID
   * @param input - イベント入力データ
   * @returns 更新後のイベント一覧とステート
   */
  async addEvent(fieldId: string, input: NewFieldEventInput): Promise<FieldEventsResponse> {
    // sequenceNumber は input が undefined の場合 null で送る (BE が自動採番する)。
    // - SEEDING / EMERGENCE / HARVEST / CULTIVATION: BE 側で 1 を強制
    // - PEST_CONTROL: FE 側で必ず明示送信される
    // - HERBICIDE: 新規追加時は null → BE が allocateNextHerbicideSequence で「既存最大 + 1」 を採番、
    //              編集時は既存 sequenceNumber を明示送信して upsert
    // 旧実装 (`?? 1`) は HERBICIDE 新規追加時に常に 1 を送ってしまい既存 sequenceNumber=1 を上書きするバグがあった。
    const body = {
      eventType: input.eventType,
      eventDate: `${input.eventDate.getFullYear()}-${String(input.eventDate.getMonth() + 1).padStart(2, '0')}-${String(input.eventDate.getDate()).padStart(2, '0')}`,
      recordType: input.recordType,
      sequenceNumber: input.sequenceNumber ?? null,
      note: input.note ?? null,
    };

    const apiResponse = await apiClient.post<FieldEventsApiResponse>(
      `/fields/${fieldId}/events`,
      body
    );

    const response: FieldEventsResponse = {
      fieldId: String(apiResponse.fieldId),
      state: apiResponse.state,
      events: apiResponse.events.map(toFieldEvent),
    };

    // キャッシュを更新
    this.cache.set(fieldId, response);
    return response;
  }

  /**
   * 圃場イベントを物理削除する (一般ユーザのアンチェック)。
   *
   * バックエンドの DELETE /api/v1/fields/{fieldId}/events/{eventId} を呼び出し、
   * 削除後の events + state を返す。 対象は PEST_CONTROL/ACTUAL のみ (それ以外は backend が 400)。
   *
   * @param fieldId - 圃場 ID
   * @param eventId - 削除対象の FieldEvent ID
   * @returns 削除後のイベント一覧とステート
   */
  async deleteEvent(fieldId: string, eventId: string): Promise<FieldEventsResponse> {
    const apiResponse = await apiClient.delete<FieldEventsApiResponse>(
      `/fields/${fieldId}/events/${eventId}`
    );

    const response: FieldEventsResponse = {
      fieldId: String(apiResponse.fieldId),
      state: apiResponse.state,
      events: apiResponse.events.map(toFieldEvent),
    };

    this.cache.set(fieldId, response);
    return response;
  }

  /**
   * 圃場の出芽予定日（EMERGENCE/SCHEDULED）を HARC API 出芽推定の最新値で上書きする。
   *
   * バックエンドの仕様により以下の通り振り分ける：
   * - 200 OK: 更新後の {@link FieldEvent} を返す（呼び出し元はストアに upsert する）
   * - 204 No Content: null を返す（EMERGENCE/ACTUAL 既登録、HARC 失敗、レスポンス空など）
   * - 400 Bad Request: SEEDING/ACTUAL 未登録など FE バグ。{@link ApiError} が伝播する
   *
   * @param fieldId - 圃場 ID
   * @returns 更新された EMERGENCE/SCHEDULED イベント、または更新が行われなかった場合は null
   */
  async refreshEmergence(fieldId: string): Promise<FieldEvent | null> {
    const apiResponse = await apiClient.postOptional<FieldEventApiResponse>(
      `/fields/${fieldId}/emergence/refresh`,
      {}
    );
    if (!apiResponse) {
      return null;
    }
    return toFieldEvent(apiResponse);
  }

  /**
   * APIからイベント一覧とステートを取得する
   * @param fieldId - 圃場 ID
   * @param asOf - ステート計算の基準日（省略時はサーバーの現在日付）
   */
  private async getFieldEventsFromApi(fieldId: string, asOf?: Date): Promise<FieldEventsResponse> {
    const params = asOf ? { asOf: formatDate(asOf) } : undefined;
    const apiResponse = await apiClient.get<FieldEventsApiResponse>(
      `/fields/${fieldId}/events`,
      params
    );

    return {
      fieldId: String(apiResponse.fieldId),
      state: apiResponse.state,
      events: apiResponse.events.map(toFieldEvent),
    };
  }
}

/** 圃場イベントサービスのシングルトンインスタンス */
export const fieldEventService = new FieldEventService();
